// src/pages/Shops.js
import React, { useState, useEffect } from 'react';
import { listenToShops, addShop, updateShop, deleteShop, listenToProducts, addProduct, updateProduct, deleteProduct } from '../services/firebase';
import toast from 'react-hot-toast';

export default function Shops() {
  const [shops, setShops] = useState([]);
  const [products, setProducts] = useState([]);
  const [selectedShop, setSelectedShop] = useState(null);
  const [tab, setTab] = useState('shops'); // shops | products
  const [showAddShop, setShowAddShop] = useState(false);
  const [showLinkMerchant, setShowLinkMerchant] = useState(false);
  const [linkMerchantId, setLinkMerchantId] = useState('');
  const [showAddProduct, setShowAddProduct] = useState(false);
  const [shopForm, setShopForm] = useState({ name: '', emoji: '🏪', deliveryTime: '20-30', rating: '4.5', active: true, merchantId: '', address: '' });
  const [productForm, setProductForm] = useState({ name: '', price: '', unit: 'kg', emoji: '🛒', category: 'Vegetables', inStock: true, shopId: '' });

  useEffect(() => {
    const u1 = listenToShops(setShops);
    const u2 = listenToProducts(setProducts);
    return () => { u1(); u2(); };
  }, []);

  const shopProducts = selectedShop ? products.filter(p => p.shopId === selectedShop.id) : products;

  const handleAddShop = async () => {
    if (!shopForm.name) { toast.error('Enter shop name'); return; }
    await addShop(shopForm);
    toast.success('Shop added!');
    setShowAddShop(false);
    setShopForm({ name: '', emoji: '🏪', deliveryTime: '20-30', rating: '4.5', active: true, merchantId: '', address: '' });
  };

  const handleAddProduct = async () => {
    if (!productForm.name || !productForm.price) { toast.error('Fill all fields'); return; }
    if (!productForm.shopId && !selectedShop) { toast.error('Select a shop first'); return; }
    await addProduct({ ...productForm, shopId: productForm.shopId || selectedShop?.id, price: Number(productForm.price) });
    toast.success('Product added!');
    setShowAddProduct(false);
    setProductForm({ name: '', price: '', unit: 'kg', emoji: '🛒', category: 'Vegetables', inStock: true, shopId: selectedShop?.id || '' });
  };

  const toggleShopActive = async (shop) => {
    await updateShop(shop.id, { active: !shop.active });
    toast(shop.active ? 'Shop deactivated' : 'Shop activated ✓');
  };

  const openLinkMerchant = (shop, e) => {
    e.stopPropagation();
    setSelectedShop(shop);
    setLinkMerchantId(shop.merchantId || '');
    setShowLinkMerchant(true);
  };

  const handleLinkMerchant = async () => {
    if (!selectedShop) return;
    if (!linkMerchantId.trim()) { toast.error('Paste merchant Firebase Auth UID'); return; }
    await updateShop(selectedShop.id, { merchantId: linkMerchantId.trim() });
    toast.success('Merchant linked to shop ✓');
    setShowLinkMerchant(false);
  };

  const toggleProductStock = async (product) => {
    await updateProduct(product.id, { inStock: !product.inStock });
  };

  return (
    <div className="page-body">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <div>
          <div style={{ fontFamily: 'Clash Display, sans-serif', fontSize: 24, fontWeight: 700, marginBottom: 4 }}>Grocery</div>
          <div style={{ fontSize: 13, color: 'var(--text2)' }}>{shops.length} shops • {products.length} products</div>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <button className="btn btn-ghost" onClick={() => setShowAddProduct(true)}>+ Add Product</button>
          <button className="btn btn-green" onClick={() => setShowAddShop(true)}>+ Add Shop</button>
        </div>
      </div>

      {/* Tab */}
      <div style={{ display: 'flex', gap: 2, background: 'var(--card)', borderRadius: 10, padding: 3, marginBottom: 20, width: 'fit-content', border: '1px solid var(--border)' }}>
        {['shops', 'products'].map(t => (
          <button key={t} onClick={() => setTab(t)} style={{ padding: '7px 20px', borderRadius: 8, border: 'none', cursor: 'pointer', fontFamily: 'Epilogue', fontSize: 13, fontWeight: 600, background: tab === t ? 'var(--green)' : 'transparent', color: tab === t ? 'var(--bg)' : 'var(--text2)', textTransform: 'capitalize', transition: 'all 0.15s' }}>
            {t}
          </button>
        ))}
      </div>

      {tab === 'shops' && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 16 }}>
          {shops.length === 0 ? (
            <div className="table-card" style={{ padding: 32, textAlign: 'center', color: 'var(--text2)', gridColumn: '1/-1' }}>No shops yet. Add your first shop!</div>
          ) : shops.map(shop => (
            <div key={shop.id} className="table-card" style={{ padding: 18, cursor: 'pointer', border: selectedShop?.id === shop.id ? '1px solid var(--green)' : '1px solid var(--border)' }} onClick={() => { setSelectedShop(shop); setTab('products'); }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 }}>
                <div style={{ fontSize: 36 }}>{shop.emoji}</div>
                <span className={`badge ${shop.active ? 'badge-green' : 'badge-gray'}`}>{shop.active ? 'Active' : 'Inactive'}</span>
              </div>
              <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 4 }}>{shop.name}</div>
              <div style={{ fontSize: 12, color: 'var(--text2)', marginBottom: 12 }}>
                ⭐ {shop.rating} • 🕐 {shop.deliveryTime} min
                {shop.merchantId
                  ? <div style={{ marginTop: 4, fontFamily: 'JetBrains Mono', fontSize: 10, color: 'var(--green)' }}>Merchant: {shop.merchantId.slice(0, 8)}…</div>
                  : <div style={{ marginTop: 4, fontSize: 10, color: 'var(--orange)' }}>⚠ No merchant linked</div>}
              </div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <button className={`btn btn-sm ${shop.active ? 'btn-red' : 'btn-green'}`} onClick={e => { e.stopPropagation(); toggleShopActive(shop); }}>
                  {shop.active ? 'Deactivate' : 'Activate'}
                </button>
                <button className="btn btn-ghost btn-sm" onClick={e => openLinkMerchant(shop, e)}>
                  {shop.merchantId ? 'Change Merchant' : 'Link Merchant'}
                </button>
                <button className="btn btn-ghost btn-sm" onClick={e => { e.stopPropagation(); deleteShop(shop.id); toast.success('Shop deleted'); }}>Delete</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === 'products' && (
        <>
          {selectedShop && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16, padding: '10px 14px', background: 'var(--green-dim)', borderRadius: 10, border: '1px solid var(--green-mid)' }}>
              <span style={{ fontSize: 20 }}>{selectedShop.emoji}</span>
              <span style={{ fontWeight: 700, fontSize: 14, color: 'var(--green)' }}>Showing products for: {selectedShop.name}</span>
              <button onClick={() => setSelectedShop(null)} style={{ marginLeft: 'auto', background: 'none', border: 'none', color: 'var(--text2)', cursor: 'pointer', fontSize: 16 }}>✕ All products</button>
            </div>
          )}
          <div className="table-card">
            <table>
              <thead>
                <tr>
                  <th>PRODUCT</th>
                  <th>CATEGORY</th>
                  <th>PRICE</th>
                  <th>UNIT</th>
                  <th>SHOP</th>
                  <th>STOCK</th>
                  <th>ACTIONS</th>
                </tr>
              </thead>
              <tbody>
                {shopProducts.length === 0 ? (
                  <tr><td colSpan={7} style={{ textAlign: 'center', color: 'var(--text2)', padding: 32 }}>No products yet. Add your first product!</td></tr>
                ) : shopProducts.map(p => {
                  const shop = shops.find(s => s.id === p.shopId);
                  return (
                    <tr key={p.id}>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                          <span style={{ fontSize: 22 }}>{p.emoji}</span>
                          <span style={{ fontWeight: 600 }}>{p.name}</span>
                        </div>
                      </td>
                      <td><span className="badge badge-blue">{p.category}</span></td>
                      <td style={{ fontFamily: 'JetBrains Mono', color: 'var(--green)', fontWeight: 700 }}>₹{p.price}</td>
                      <td style={{ color: 'var(--text2)' }}>/{p.unit}</td>
                      <td>{shop ? `${shop.emoji} ${shop.name}` : '—'}</td>
                      <td>
                        <div onClick={() => toggleProductStock(p)} style={{ cursor: 'pointer' }}>
                          <span className={`badge ${p.inStock ? 'badge-green' : 'badge-red'}`}>{p.inStock ? 'In Stock' : 'Out'}</span>
                        </div>
                      </td>
                      <td>
                        <button className="btn btn-ghost btn-sm" style={{ color: 'var(--red)', borderColor: 'rgba(255,23,68,0.2)' }} onClick={() => { deleteProduct(p.id); toast.success('Product deleted'); }}>Delete</button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* Link Merchant Modal */}
      {showLinkMerchant && selectedShop && (
        <div className="modal-overlay" onClick={() => setShowLinkMerchant(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-title">Link Merchant to {selectedShop.name}</div>
            <div style={{ fontSize: 13, color: 'var(--text2)', marginBottom: 16, lineHeight: 1.5 }}>
              Paste the merchant&apos;s Firebase Auth UID from the merchant app (&quot;Copy ID for Admin&quot;) or Firebase Console → Authentication.
            </div>
            <div className="form-group">
              <label className="form-label">MERCHANT UID</label>
              <input
                placeholder="e.g. XSzBZPS5DnTl5da2jrsDiAd74p73"
                value={linkMerchantId}
                onChange={e => setLinkMerchantId(e.target.value.trim())}
              />
            </div>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 8 }}>
              <button className="btn btn-ghost" onClick={() => setShowLinkMerchant(false)}>Cancel</button>
              <button className="btn btn-green" onClick={handleLinkMerchant}>Save Link</button>
            </div>
          </div>
        </div>
      )}

      {/* Add Shop Modal */}
      {showAddShop && (
        <div className="modal-overlay" onClick={() => setShowAddShop(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-title">Add New Shop</div>
            <div className="form-group">
              <label className="form-label">SHOP NAME</label>
              <input placeholder="e.g. Sri Balaji Supermarket" value={shopForm.name} onChange={e => setShopForm({ ...shopForm, name: e.target.value })} />
            </div>
            <div className="form-group">
              <label className="form-label">SHOP ADDRESS</label>
              <input placeholder="Main Road, Piler" value={shopForm.address} onChange={e => setShopForm({ ...shopForm, address: e.target.value })} />
            </div>
            <div className="form-group">
              <label className="form-label">MERCHANT UID (Firebase Auth)</label>
              <input placeholder="Paste merchant Firebase Auth UID" value={shopForm.merchantId} onChange={e => setShopForm({ ...shopForm, merchantId: e.target.value.trim() })} />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div className="form-group">
                <label className="form-label">EMOJI</label>
                <input placeholder="🏪" value={shopForm.emoji} onChange={e => setShopForm({ ...shopForm, emoji: e.target.value })} />
              </div>
              <div className="form-group">
                <label className="form-label">DELIVERY TIME</label>
                <input placeholder="20-30" value={shopForm.deliveryTime} onChange={e => setShopForm({ ...shopForm, deliveryTime: e.target.value })} />
              </div>
              <div className="form-group">
                <label className="form-label">RATING</label>
                <input placeholder="4.5" value={shopForm.rating} onChange={e => setShopForm({ ...shopForm, rating: e.target.value })} />
              </div>
            </div>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 8 }}>
              <button className="btn btn-ghost" onClick={() => setShowAddShop(false)}>Cancel</button>
              <button className="btn btn-green" onClick={handleAddShop}>Add Shop</button>
            </div>
          </div>
        </div>
      )}

      {/* Add Product Modal */}
      {showAddProduct && (
        <div className="modal-overlay" onClick={() => setShowAddProduct(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-title">Add New Product</div>
            <div className="form-group">
              <label className="form-label">SHOP</label>
              <select value={productForm.shopId || selectedShop?.id || ''} onChange={e => setProductForm({ ...productForm, shopId: e.target.value })}>
                <option value="">Select a shop</option>
                {shops.map(s => <option key={s.id} value={s.id}>{s.emoji} {s.name}</option>)}
              </select>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div className="form-group">
                <label className="form-label">PRODUCT NAME</label>
                <input placeholder="e.g. Tomatoes" value={productForm.name} onChange={e => setProductForm({ ...productForm, name: e.target.value })} />
              </div>
              <div className="form-group">
                <label className="form-label">EMOJI</label>
                <input placeholder="🍅" value={productForm.emoji} onChange={e => setProductForm({ ...productForm, emoji: e.target.value })} />
              </div>
              <div className="form-group">
                <label className="form-label">PRICE (₹)</label>
                <input type="number" placeholder="40" value={productForm.price} onChange={e => setProductForm({ ...productForm, price: e.target.value })} />
              </div>
              <div className="form-group">
                <label className="form-label">UNIT</label>
                <select value={productForm.unit} onChange={e => setProductForm({ ...productForm, unit: e.target.value })}>
                  {['kg', 'g', 'litre', 'piece', 'dozen', 'pack', 'bunch'].map(u => <option key={u}>{u}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">CATEGORY</label>
                <select value={productForm.category} onChange={e => setProductForm({ ...productForm, category: e.target.value })}>
                  {['Vegetables', 'Fruits', 'Dairy', 'Grains', 'Snacks', 'Beverages', 'Meat', 'Other'].map(c => <option key={c}>{c}</option>)}
                </select>
              </div>
            </div>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 8 }}>
              <button className="btn btn-ghost" onClick={() => setShowAddProduct(false)}>Cancel</button>
              <button className="btn btn-green" onClick={handleAddProduct}>Add Product</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
